/**
 * ActionCard Component
 * 
 * A reusable card component for displaying action items in the employee menu.
 * Each card represents a procedure or action the employee can perform.
 * 
 * Features:
 * - Icon on the left side
 * - Label text describing the action
 * - Clickable button that triggers the action
 * - Touch-friendly design optimized for mobile use
 */
import React from "react";

interface ActionCardProps {
  /** Source path for the action icon image */
  iconSrc: string;
  /** Japanese label text describing the action */
  label: string;
  /** Optional badge count shown on the right (e.g. resubmission needed). */
  badgeCount?: number;
  /** Render unread indicator as count or simple dot. */
  badgeVariant?: 'count' | 'dot';
  /** Disable the card (no click). */
  disabled?: boolean;
  /** Callback function called when the card is clicked */
  onClick: () => void;
}

const ActionCard: React.FC<ActionCardProps> = ({
  iconSrc,
  label,
  badgeCount,
  badgeVariant = 'count',
  disabled = false,
  onClick,
}) => {
  return (
    <button className="action-card" onClick={onClick} disabled={disabled} aria-disabled={disabled}>
      {/* Action icon displayed on the left */}
      <img src={iconSrc} alt="" className="action-icon" />
      {/* Action label text describing what this card does */}
      <span className="action-label">{label}</span>
      {typeof badgeCount === 'number' && badgeCount > 0 && (
        <span
          className={`action-badge${badgeVariant === 'dot' ? ' action-badge--dot' : ''}`}
          aria-label={badgeVariant === 'dot' ? 'unread' : `${badgeCount}`}
        >
          {badgeVariant === 'dot' ? '' : badgeCount}
        </span>
      )}
    </button>
  );
};

export default ActionCard;
